import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import API from '../config';
import { Helmet } from 'react-helmet-async';
import AddPhoneModal from '../components/AddPhoneModal';

const Step = ({ n, current, T }) => (
  <div style={{ display: 'flex', alignItems: 'center', flex: n < 3 ? 1 : 'none' }}>
    <div style={{
      width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
      background: current >= n ? T.accent : T.surfaceHi,
      border: `2px solid ${current >= n ? T.accent : T.border}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: current >= n ? '#fff' : T.textDim, fontWeight: 800, fontSize: 14,
      transition: 'all 0.3s',
    }}>
      {current > n ? '✓' : n}
    </div>
    {n < 3 && (
      <div style={{ flex: 1, height: 2, margin: '0 8px', transition: 'background 0.3s', background: current > n ? T.accent : T.border }} />
    )}
  </div>
);

// pending   — transfer accepted, waiting for M-Pesa delivery
// completed — M-Pesa confirmed funds delivered
// failed    — transfer failed; balance already refunded by backend
// timeout   — polled 3 min with no terminal status

const POLL_INTERVAL_MS = 4000;
const POLL_TIMEOUT_MS  = 180000;

export default function Withdraw() {
  const [amount, setAmount]                 = useState('');
  const [phone, setPhone]                   = useState('');
  const [balance, setBalance]               = useState(0);
  const [step, setStep]                     = useState(1);
  const [loading, setLoading]               = useState(false);
  const [fetching, setFetching]             = useState(true);
  const [error, setError]                   = useState('');
  const [withdrawalRef, setWithdrawalRef]   = useState(null);
  const [txStatus, setTxStatus]             = useState('pending');
  const [amountReceived, setAmountReceived] = useState(0);

  const pollTimer = useRef(null);
  const pollStart = useRef(null);
  const isMounted = useRef(true);

  const [showPhoneModal, setShowPhoneModal] = useState(false);

  const { token } = useAuth();
  const { T }     = useTheme();
  const navigate  = useNavigate();

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; clearTimeout(pollTimer.current); };
  }, []);

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    axios.get(`${API}/api/users/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        setBalance(parseFloat(res.data.balance || 0));
        if (res.data.phone) setPhone(res.data.phone);
      })
      .catch(() => navigate('/login'))
      .finally(() => setFetching(false));
  }, [token]);

  // ✅ FIXED: Polls /api/paystack/withdraw/status/:ref (not the deposit endpoint)
  useEffect(() => {
    if (step !== 3 || !withdrawalRef) return;
    pollStart.current = Date.now();

    const poll = async () => {
      if (!isMounted.current) return;
      if (txStatus === 'completed' || txStatus === 'failed') return;
      if (Date.now() - pollStart.current > POLL_TIMEOUT_MS) {
        if (isMounted.current) setTxStatus('timeout');
        return;
      }
      try {
        const res = await axios.get(
  `${API}/api/mpesa/withdrawal-status/${encodeURIComponent(withdrawalRef)}`,
  { headers: { Authorization: `Bearer ${token}` } },
);
        if ((res.data.status === 'completed' || res.data.status === 'failed') && isMounted.current) {
          setTxStatus(res.data.status);
          return;
        }
      } catch (err) {
        console.warn('[poll]', err.message);
      }
      if (isMounted.current) pollTimer.current = setTimeout(poll, POLL_INTERVAL_MS);
    };

    pollTimer.current = setTimeout(poll, POLL_INTERVAL_MS);
    return () => clearTimeout(pollTimer.current);
  }, [step, withdrawalRef]);

  const handleWithdraw = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await axios.post(
  `${API}/api/transactions/withdraw`,
  { amount: parseFloat(amount) },
  { headers: { Authorization: `Bearer ${token}` } },
);
setWithdrawalRef(res.data.reference);
setAmountReceived(res.data.amount_to_receive);
      setTxStatus('pending');
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.message || 'Withdrawal failed. Please try again.');
    } finally { setLoading(false); }
  };

  const amt             = parseFloat(amount) || 0;
  const WITHDRAWAL_FEE  = amt > 110 ? parseFloat((amt * 0.02).toFixed(2)) : 0;
  const previewReceived = Math.max(0, amt - WITHDRAWAL_FEE);
  const amountValid     = amt >= 100 && amt <= balance;
  const quickAmounts    = [500, 1000, 2500, 5000].filter(a => a <= balance);

  const inputStyle = {
    width: '100%', background: T.surfaceHi, border: `1px solid ${T.border}`,
    borderRadius: 8, padding: '13px 14px', color: T.text, fontSize: 14,
    outline: 'none', boxSizing: 'border-box', fontFamily: "'DM Sans',sans-serif",
    transition: 'border-color 0.2s',
  };

  if (fetching) return (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ color: T.textDim, fontFamily: "'DM Mono',monospace", fontSize: 13 }}>Loading...</p>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: T.bg, fontFamily: "'DM Sans','Segoe UI',sans-serif", color: T.text }}>
      <Helmet>
  <meta name="robots" content="noindex, nofollow" />
</Helmet>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500;700&display=swap');
        @keyframes fadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
        @keyframes popIn  { from{opacity:0;transform:scale(0.8)} to{opacity:1;transform:scale(1)} }
        @keyframes spin   { to{transform:rotate(360deg)} }
        input:focus { border-color:${T.accent}!important; box-shadow:0 0 0 3px ${T.accent}15; }
        *{box-sizing:border-box;margin:0;padding:0;}
        input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none;}
      `}</style>

      <nav style={{ background:`${T.bg}f2`, backdropFilter:'blur(16px)', borderBottom:`1px solid ${T.border}`, height:56, display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 24px', position:'sticky', top:0, zIndex:100 }}>
        <div onClick={() => navigate('/markets')} style={{ cursor:'pointer' }}>
          <svg width="100" height="32" viewBox="0 0 120 38" fill="none">
            <rect x="1" y="1" width="36" height="36" rx="9" fill="#131416" stroke="#2a2b2f" strokeWidth="1.5"/>
            <line x1="11" y1="30" x2="20" y2="8" stroke="#3772ff" strokeWidth="3" strokeLinecap="round"/>
            <line x1="19" y1="30" x2="28" y2="8" stroke="#16c784" strokeWidth="3" strokeLinecap="round"/>
            <text x="46" y="20" dominantBaseline="middle" fontFamily="'DM Sans',sans-serif" fontWeight="800" fontSize="22" fill={T.text} letterSpacing="-1">mino</text>
          </svg>
        </div>
        <button onClick={() => navigate('/dashboard')} style={{ background:'transparent', color:T.textMuted, border:`1px solid ${T.border}`, borderRadius:7, padding:'6px 14px', cursor:'pointer', fontSize:13 }}>← Dashboard</button>
      </nav>

      <div style={{ maxWidth:480, margin:'0 auto', padding:'48px 24px', animation:'fadeUp 0.4s ease' }}>
        <div style={{ display:'flex', alignItems:'center', marginBottom:48 }}>
          <Step n={1} current={step} T={T} />
          <Step n={2} current={step} T={T} />
          <Step n={3} current={step} T={T} />
        </div>

        {/* ── Step 1 — Amount ── */}
        {step === 1 && (
          <div>
            <h2 style={{ fontSize:26, fontWeight:800, letterSpacing:'-0.5px', marginBottom:6 }}>Withdraw Funds</h2>
            <p style={{ color:T.textMuted, marginBottom:28, fontSize:14 }}>Send your winnings to M-Pesa</p>

            <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:12, padding:20, marginBottom:24, position:'relative', overflow:'hidden' }}>
              <div style={{ position:'absolute', top:0, right:0, width:100, height:100, background:`radial-gradient(circle at top right, ${T.yes}15, transparent 70%)` }}/>
              <p style={{ color:T.textDim, fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:6 }}>Available Balance</p>
              <p style={{ color:T.yes, fontSize:30, fontWeight:800, letterSpacing:'-1px', fontFamily:"'DM Mono',monospace" }}>KES {balance.toLocaleString()}</p>
            </div>

            {balance === 0 && (
              <div style={{ background:`${T.no}12`, border:`1px solid ${T.no}33`, color:T.no, padding:'12px 14px', borderRadius:8, marginBottom:20, fontSize:13 }}>
                Your balance is KES 0. Deposit funds before withdrawing.
              </div>
            )}

            {quickAmounts.length > 0 && (
              <>
                <p style={{ color:T.textDim, fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:10 }}>Quick Select</p>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:8, marginBottom:18 }}>
                  {quickAmounts.map(a => (
                    <button key={a} onClick={() => setAmount(a.toString())} style={{
                      padding:14, borderRadius:8, fontFamily:"'DM Mono',monospace",
                      border:`1px solid ${amount===a.toString()?T.accent:T.border}`,
                      background:amount===a.toString()?`${T.accent}15`:T.surfaceHi,
                      color:amount===a.toString()?T.accent:T.textMuted,
                      fontWeight:700, cursor:'pointer', fontSize:13, transition:'all 0.15s',
                    }}>KES {a.toLocaleString()}</button>
                  ))}
                </div>
              </>
            )}

            <div style={{ marginBottom:8 }}>
              <label style={{ color:T.textDim, fontSize:11, display:'block', marginBottom:6, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em' }}>Custom Amount</label>
              <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Enter amount in KES" min="100" max={balance} style={inputStyle}/>
            </div>

            {amount && parseFloat(amount) > balance && <p style={{ color:T.no, fontSize:13, marginBottom:12 }}>⚠️ Exceeds your available balance</p>}
            {amount && parseFloat(amount) < 100 && <p style={{ color:T.no, fontSize:13, marginBottom:12 }}>⚠️ Minimum withdrawal is KES 100</p>}

            <button onClick={() => { if (amountValid) { if (!phone) { setShowPhoneModal(true); return; } setStep(2); } }} disabled={!amountValid} style={{
              width:'100%', background:amountValid?T.accent:T.surfaceHi, color:amountValid?'#fff':T.textDim,
              border:'none', borderRadius:8, padding:14, fontSize:14, fontWeight:700,
              cursor:amountValid?'pointer':'not-allowed', transition:'all 0.2s', marginTop:12,
            }}>Continue →</button>
          </div>
        )}

        {/* ── Step 2 — Confirm ── */}
        {step === 2 && (
          <div>
            <h2 style={{ fontSize:26, fontWeight:800, letterSpacing:'-0.5px', marginBottom:6 }}>Confirm Withdrawal</h2>
            <p style={{ color:T.textMuted, marginBottom:28, fontSize:14 }}>Funds will be sent directly to your M-Pesa</p>

            <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:12, padding:'18px 20px', marginBottom:24 }}>
              {[
                { label:'Withdrawal amount', value:`KES ${amt.toLocaleString()}`, color:T.text },
                { label:`Withdrawal fee (2%)${amt<=110?' — waived':''}`, value:amt>110?`-KES ${WITHDRAWAL_FEE}`:'KES 0', color:amt>110?T.no:T.textDim },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                  <span style={{ color:T.textMuted, fontSize:13 }}>{label}</span>
                  <span style={{ color, fontWeight:600, fontSize:13, fontFamily:"'DM Mono',monospace" }}>{value}</span>
                </div>
              ))}
              <div style={{ borderTop:`1px solid ${T.border}`, paddingTop:12, marginTop:4, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ color:T.text, fontSize:14, fontWeight:700 }}>You receive</span>
                <span style={{ color:T.yes, fontWeight:800, fontSize:22, fontFamily:"'DM Mono',monospace" }}>KES {previewReceived.toLocaleString()}</span>
              </div>
            </div>

            <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:8, padding:'14px 16px', marginBottom:24 }}>
              <p style={{ color:T.textDim, fontSize:11, marginBottom:4, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em' }}>Sending to</p>
              <p style={{ color:T.text, fontSize:15, fontWeight:700 }}>📱 {phone}</p>
              <p style={{ color:T.textDim, fontSize:12, marginTop:4 }}>Your verified M-Pesa number</p>
            </div>

            {error && <div style={{ background:`${T.no}12`, border:`1px solid ${T.no}33`, color:T.no, padding:'12px 14px', borderRadius:8, marginBottom:20, fontSize:13 }}>{error}</div>}

            <form onSubmit={handleWithdraw}>
              <button type="submit" disabled={loading||!phone} style={{
                width:'100%', background:loading||!phone?T.surfaceHi:T.accent, color:loading||!phone?T.textDim:'#fff',
                border:'none', borderRadius:8, padding:14, fontSize:14, fontWeight:700,
                cursor:loading||!phone?'not-allowed':'pointer', marginBottom:10, transition:'all 0.2s',
              }}>{loading?'Processing...':'💸 Withdraw to M-Pesa'}</button>
              <button type="button" onClick={() => { setStep(1); setError(''); }} style={{ width:'100%', background:'transparent', color:T.textMuted, border:`1px solid ${T.border}`, borderRadius:8, padding:13, cursor:'pointer', fontSize:13 }}>← Back</button>
            </form>
          </div>
        )}

        {/* ── Step 3 — Status ── */}
        {step === 3 && (
          <div style={{ textAlign:'center', animation:'popIn 0.4s ease' }}>
            {txStatus === 'pending' && (
              <>
                <div style={{ width:80, height:80, margin:'0 auto 28px', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <div style={{ width:56, height:56, border:`4px solid ${T.border}`, borderTop:`4px solid ${T.accent}`, borderRadius:'50%', animation:'spin 0.9s linear infinite' }}/>
                </div>
                <h2 style={{ fontSize:26, fontWeight:800, marginBottom:10 }}>Processing…</h2>
                <p style={{ color:T.textMuted, fontSize:14, marginBottom:8 }}>Sending KES {amountReceived.toLocaleString()} to your M-Pesa.</p>
                <p style={{ color:T.textDim, fontSize:13, fontFamily:"'DM Mono',monospace" }}>This usually takes under a minute.</p>
              </>
            )}
            {txStatus === 'completed' && (
              <>
                <div style={{ width:80, height:80, background:`${T.yes}15`, border:`2px solid ${T.yes}44`, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:36, margin:'0 auto 28px' }}>💸</div>
                <h2 style={{ fontSize:26, fontWeight:800, marginBottom:10 }}>Sent!</h2>
                <p style={{ color:T.textMuted, fontSize:14, marginBottom:8 }}>KES {amountReceived.toLocaleString()} is on its way to your M-Pesa.</p>
                <p style={{ color:T.textDim, fontSize:13, marginBottom:40, fontFamily:"'DM Mono',monospace" }}>{phone}</p>
                <button onClick={() => navigate('/dashboard')} style={{ background:T.accent, color:'#fff', border:'none', borderRadius:8, padding:'14px 40px', fontSize:14, fontWeight:700, cursor:'pointer' }}>Go to Dashboard →</button>
              </>
            )}
            {txStatus === 'failed' && (
              <>
                <div style={{ width:80, height:80, background:`${T.no}15`, border:`2px solid ${T.no}44`, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:36, margin:'0 auto 28px' }}>❌</div>
                <h2 style={{ fontSize:26, fontWeight:800, marginBottom:10 }}>Withdrawal Failed</h2>
                <p style={{ color:T.textMuted, fontSize:14, marginBottom:8 }}>M-Pesa could not process this payment. Your balance has been refunded.</p>
                <p style={{ color:T.textDim, fontSize:13, marginBottom:40 }}>Please check your M-Pesa number and try again.</p>
                <div style={{ display:'flex', gap:12, justifyContent:'center' }}>
                  <button onClick={() => { setStep(1); setError(''); setTxStatus('pending'); }} style={{ background:T.accent, color:'#fff', border:'none', borderRadius:8, padding:'14px 28px', fontSize:14, fontWeight:700, cursor:'pointer' }}>Try Again</button>
                  <button onClick={() => navigate('/dashboard')} style={{ background:'transparent', color:T.textMuted, border:`1px solid ${T.border}`, borderRadius:8, padding:'14px 28px', fontSize:14, cursor:'pointer' }}>Dashboard</button>
                </div>
              </>
            )}
            {txStatus === 'timeout' && (
              <>
                <div style={{ width:80, height:80, background:`${T.accent}15`, border:`2px solid ${T.accent}44`, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:36, margin:'0 auto 28px' }}>⏳</div>
                <h2 style={{ fontSize:26, fontWeight:800, marginBottom:10 }}>Still Processing</h2>
                <p style={{ color:T.textMuted, fontSize:14, marginBottom:8 }}>This is taking longer than usual. Your funds are safe.</p>
                <p style={{ color:T.textDim, fontSize:13, marginBottom:40 }}>Check your transaction history for the final status, or wait for an M-Pesa SMS.</p>
                <button onClick={() => navigate('/dashboard')} style={{ background:T.accent, color:'#fff', border:'none', borderRadius:8, padding:'14px 40px', fontSize:14, fontWeight:700, cursor:'pointer' }}>Go to Dashboard →</button>
              </>
            )}
          </div>
        )}
      </div>
      {showPhoneModal && (
        <AddPhoneModal
          onSuccess={(savedPhone) => {
            setPhone(savedPhone);
            setShowPhoneModal(false);
            setStep(2);
          }}
          onClose={() => setShowPhoneModal(false)}
        />
      )}
    
    </div>
  );
}